import React from "react";
import { Link } from "react-router-dom";

export function Container({ className = "", children }) {
  return <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>;
}

export function Kicker({ children, className = "" }) {
  return (
    <div
      className={`font-sans text-xs uppercase tracking-[0.25em] font-semibold text-[#CDA434] ${className}`}
    >
      {children}
    </div>
  );
}

export function SectionHeading({ kicker, title, intro, align = "left", className = "" }) {
  const alignCls = align === "center" ? "text-center mx-auto" : "text-left";
  return (
    <div className={`max-w-2xl ${alignCls} ${className}`}>
      {kicker && <Kicker className="mb-3">{kicker}</Kicker>}
      <h2 className="font-serif font-semibold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#1E1E2F] leading-[1.15]">
        {title}
      </h2>
      {intro && <p className="mt-4 font-sans text-base text-[#505A68] leading-relaxed">{intro}</p>}
    </div>
  );
}

export function GoldRule({ className = "" }) {
  return <div className={`gold-rule ${className}`} />;
}

const shoreLabels = {
  lincolnshire: "Lincolnshire",
  massachusetts: "Massachusetts",
  both: "Both shores",
};

export function ShoreBadge({ shore }) {
  const isMass = shore === "massachusetts";
  const isBoth = shore === "both";
  const cls = isBoth
    ? "bg-[#CDA434]/15 text-[#7a611c] border-[#CDA434]/50"
    : isMass
    ? "bg-[#1E1E2F]/10 text-[#1E1E2F] border-[#1E1E2F]/30"
    : "bg-[#003366]/10 text-[#003366] border-[#003366]/30";
  return (
    <span
      data-testid={`shore-badge-${shore}`}
      className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium border ${cls}`}
    >
      {shoreLabels[shore] || shore}
    </span>
  );
}

const planMeta = {
  harbour: { label: "Harbour", show: false },
  stump: { label: "Stump member", show: true },
  beacon: { label: "Beacon", show: true },
  crossing: { label: "Crossing", show: true },
};

export function PlanBadge({ plan }) {
  const meta = planMeta[plan];
  if (!meta || !meta.show) return null;
  return (
    <span
      data-testid={`plan-badge-${plan}`}
      className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider bg-[#CDA434]/15 text-[#7a611c] border border-[#CDA434]/60"
    >
      <span className="w-1.5 h-1.5 bg-[#CDA434] rounded-full" />
      {meta.label}
    </span>
  );
}

export function AdvertBadge({ label = "Advert" }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] bg-[#1E1E2F]/10 text-[#505A68] border border-[#D6D3CB]">
      {label}
    </span>
  );
}

export function PrimaryButton({ as = "button", to, className = "", children, ...props }) {
  const cls = `inline-flex items-center justify-center bg-[#1E1E2F] hover:bg-[#003366] text-[#FFFAF3] font-sans font-semibold text-sm px-6 py-3 tracking-wide transition-colors border border-transparent shadow-sm ${className}`;
  if (as === "link") return <Link to={to} className={cls} {...props}>{children}</Link>;
  return <button className={cls} {...props}>{children}</button>;
}

export function GoldButton({ as = "button", to, className = "", children, ...props }) {
  const cls = `inline-flex items-center justify-center bg-[#CDA434] hover:bg-[#b8912b] text-[#1E1E2F] font-sans font-semibold text-sm px-6 py-3 tracking-wide transition-colors shadow-sm ${className}`;
  if (as === "link") return <Link to={to} className={cls} {...props}>{children}</Link>;
  return <button className={cls} {...props}>{children}</button>;
}

export function OutlineButton({ as = "button", to, className = "", children, ...props }) {
  const cls = `inline-flex items-center justify-center bg-transparent border border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-[#FFFAF3] font-sans font-medium text-sm px-5 py-2.5 transition-colors ${className}`;
  if (as === "link") return <Link to={to} className={cls} {...props}>{children}</Link>;
  return <button className={cls} {...props}>{children}</button>;
}

export function EmptyState({ icon, heading, text, testId }) {
  return (
    <div
      data-testid={testId}
      className="text-center py-16 px-6 bg-[#FFFAF3] border border-dashed border-[#D6D3CB] my-6"
    >
      {icon && (
        <div className="w-12 h-12 mx-auto mb-4 border border-[#CDA434] flex items-center justify-center text-[#CDA434] bg-[#F8F1E7]">
          {icon}
        </div>
      )}
      <h3 className="font-serif text-xl font-semibold text-[#1E1E2F] mb-2">{heading}</h3>
      {text && <p className="text-sm text-[#505A68] max-w-md mx-auto">{text}</p>}
    </div>
  );
}
